import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Flex,
    Heading,
    Select,
    Table,
    TableContainer,
    Tag,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useBreakpointValue,
    useToast,
    VStack
} from '@chakra-ui/react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { templatePriceService, TemplatePriceRow } from '../services/templatePriceService';

// View-only: template price management is a separate task.
export const TemplatePricesPage: React.FC = () => {
    const [levels, setLevels] = useState<string[]>([]);
    const [selectedLevel, setSelectedLevel] = useState<string>('');
    const [rows, setRows] = useState<TemplatePriceRow[]>([]);
    const [isLoadingRows, setIsLoadingRows] = useState(false);
    const [selectedMarketType, setSelectedMarketType] = useState<string>('');

    const toast = useToast();
    const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });

    useEffect(() => {
        let cancelled = false;
        templatePriceService.getLevels()
            .then(loaded => {
                if (cancelled) return;
                setLevels(loaded);
                setSelectedLevel(loaded[0] ?? '');
            })
            .catch(() => {
                if (cancelled) return;
                toast({ title: 'Failed to load margin levels', status: 'error', duration: 3000, isClosable: true });
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedLevel) {
            setRows([]);
            return;
        }
        let cancelled = false;
        setIsLoadingRows(true);
        templatePriceService.getPrices(selectedLevel)
            .then(loaded => {
                if (cancelled) return;
                setRows(loaded);
                setSelectedMarketType(current =>
                    current && loaded.some(r => r.marketType === current) ? current : loaded[0]?.marketType ?? '');
            })
            .catch(() => {
                if (cancelled) return;
                setRows([]);
                toast({ title: 'Failed to load template prices', status: 'error', duration: 3000, isClosable: true });
            })
            .finally(() => { if (!cancelled) setIsLoadingRows(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLevel]);

    const marketTypeCounts = useMemo(() => {
        const counts = new Map<string, number>();
        rows.forEach(row => counts.set(row.marketType, (counts.get(row.marketType) ?? 0) + 1));
        return Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [rows]);

    const visibleRows = useMemo(
        () => rows.filter(row => row.marketType === selectedMarketType),
        [rows, selectedMarketType]
    );

    return (
        <Box p={{ base: 2, md: 4 }}>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Heading size={headingSize}>Template Prices</Heading>
                <Text fontSize="sm" color="gray.500">
                    Read-only view of the margin-baked probability→price sets. Which set prices a
                    competition is shown on its events; management is handled separately.
                </Text>

                <Box bg="white" p={4} borderRadius="md" shadow="sm">
                    <VStack align="stretch" spacing={3}>
                        <Flex direction={{ base: 'column', md: 'row' }} gap={3} align={{ base: 'stretch', md: 'center' }}>
                            <Heading size="xs" whiteSpace="nowrap">Margin level</Heading>
                            <Select
                                value={selectedLevel}
                                onChange={(e) => setSelectedLevel(e.target.value)}
                                size={buttonSize}
                                maxWidth={{ base: '100%', md: '240px' }}
                                placeholder={levels.length === 0 ? 'No levels loaded' : undefined}
                            >
                                {levels.map(level => <option key={level} value={level}>{level}</option>)}
                            </Select>
                            <Select
                                value={selectedMarketType}
                                onChange={(e) => setSelectedMarketType(e.target.value)}
                                size={buttonSize}
                                maxWidth={{ base: '100%', md: '260px' }}
                                isDisabled={rows.length === 0}
                            >
                                {marketTypeCounts.map(([marketType]) => (
                                    <option key={marketType} value={marketType}>{marketType}</option>
                                ))}
                            </Select>
                            <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                                {rows.length} prices in level
                            </Text>
                        </Flex>

                        {rows.length > 0 && (
                            <Flex flexWrap="wrap" gap={2}>
                                {marketTypeCounts.map(([marketType, count]) => (
                                    <Tag
                                        key={marketType}
                                        size="sm"
                                        colorScheme={marketType === selectedMarketType ? 'blue' : 'gray'}
                                        cursor="pointer"
                                        onClick={() => setSelectedMarketType(marketType)}
                                    >
                                        {marketType}: {count}
                                    </Tag>
                                ))}
                            </Flex>
                        )}

                        {isLoadingRows ? (
                            <LoadingSpinner message="Loading template prices..." />
                        ) : visibleRows.length === 0 ? (
                            <EmptyState message={levels.length === 0
                                ? 'No template prices loaded yet.'
                                : 'No prices for the selected level and market type.'} />
                        ) : (
                            <TableContainer maxHeight="60vh" overflowY="auto">
                                <Table size="sm" variant="striped">
                                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                                        <Tr>
                                            <Th>Market type</Th>
                                            <Th isNumeric>Probability</Th>
                                            <Th isNumeric>Price</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {visibleRows.map((row, index) => (
                                            <Tr key={`${row.marketType}-${row.probability}-${index}`}>
                                                <Td>{row.marketType}</Td>
                                                <Td isNumeric>{row.probability}</Td>
                                                <Td isNumeric>{row.price}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};
